import React, { useCallback, useState, useMemo,useEffect } from "react";
import {
  CheckCircle2,
  ShieldCheck,
  Loader2,
  Lock,
  CheckCircle,
  RefreshCw,
  AlertCircle,
  X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";

import SummaryApi from "../common";
import PaymentModal from "../pages/PaymentModal";
import { useLocation, useNavigate } from "react-router-dom";

const PackageCard = ({
  plan_id,
  package_id,
  name,
  actual_price,
  selling_price,
  duration,
  rate,
  padCount,
  title,
  subtitle,
  features = "",
  specifications = "",
  popular = false,
  onSubscribeSuccess,
  isSubscribed,
  isUsed,
  canSubscribe,
}) => {
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false); // New state for Auth Modal
  const navigate = useNavigate();
  const location = useLocation();
  const processedFeatures = useMemo(() => {
    // Pick whichever prop has the data (handles prop naming mismatch)
    const rawData = specifications || features || "";

    if (!rawData) return [];
    if (Array.isArray(rawData)) return rawData;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(rawData, "text/html");

      // Look for ALL list items (works for both <ul> and <ol>)
      const lis = doc.querySelectorAll("li");
      if (lis.length > 0) {
        return Array.from(lis)
          .map((li) => li.innerHTML.trim())
          .filter(Boolean);
      }

      // Fallback for paragraph-based lists
      const ps = doc.querySelectorAll("p");
      if (ps.length > 0) {
        return Array.from(ps)
          .map((p) => p.innerHTML.trim())
          .filter((content) => content !== "" && content !== "<br>");
      }

      return [rawData];
    } catch (e) {
      console.error("Parsing error:", e);
      return [rawData];
    }
  }, [specifications, features]);

  const isPackagesRoute = location.pathname === "/packages";

  // 2. SLICE FOR PREVIEW (Important: use processedFeatures array)
  const displayedFeatures = isPackagesRoute
    ? processedFeatures
    : processedFeatures.slice(0, 4);

  const getUserId = useCallback(() => {
    const sessionData = localStorage.getItem("user");
    if (!sessionData) return null;
    try {
      const parsed = JSON.parse(sessionData);
      return parsed.user_id || parsed.id || parsed;
    } catch (e) {
      return sessionData;
    }
  }, []);
useEffect(() => {
  // Check if there's a pending subscription intent for THIS package
  const pendingPkgId = localStorage.getItem("pending_subscription_id");
  const user = getUserId();

  if (user && pendingPkgId && String(pendingPkgId) === String(package_id)) {
    // If user is now logged in and this is the package they clicked
    setIsModalOpen(true);
    // Remove it so it doesn't pop up again on refresh
    localStorage.removeItem("pending_subscription_id");
  }
}, [package_id, getUserId]);
const handleAuthNavigation = (path) => {
  // Save the intent before leaving
  localStorage.setItem("pending_subscription_id", package_id);
  navigate(path);
};
  // Updated Toast logic (removed 'auth' type from here as it's now a modal)
  const showCustomToast = (type, title, message, action = null) => {
    toast.dismiss();
    toast.custom((t) => (
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className={`${
          t.visible ? "animate-enter" : "animate-leave"
        } max-w-md w-full bg-white shadow-2xl rounded-2xl pointer-events-auto flex border-b-4 overflow-hidden
          ${type === "success" ? "border-green-500" : "border-rose-500"}`}
      >
        <div className="flex-1 p-4">
          <div className="flex items-start gap-3">
            <div
              className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${type === "success" ? "bg-green-100 text-green-600" : "bg-rose-100 text-rose-600"}`}
            >
              {type === "success" ? (
                <CheckCircle size={24} />
              ) : (
                <AlertCircle size={24} />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-gray-900">{title}</p>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                {message}
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-col border-l border-gray-50 w-24">
          {action ? (
            <button
              onClick={() => {
                toast.dismiss(t.id);
                action.fn();
              }}
              className="flex-1 px-2 text-xs font-bold text-blue-600 hover:bg-gray-50 border-b border-gray-50"
            >
              {action.label}
            </button>
          ) : null}
          <button
            onClick={() => toast.dismiss(t.id)}
            className="flex-1 px-2 text-xs font-bold text-gray-400 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </motion.div>
    ));
  };

 const calculateEndDate = (startDateStr, durationValue) => {
  if (!durationValue) return startDateStr;
  
  const startDate = new Date(startDateStr);
  const endDate = new Date(startDate);
  
  // Convert duration to a number (it comes as "3" or "6" from your JSON)
  const monthsToAdd = parseInt(durationValue, 10);

  if (!isNaN(monthsToAdd)) {
    endDate.setMonth(endDate.getMonth() + monthsToAdd);
    
    // Fix for month-end overflow (e.g., Jan 31 + 1 month should be Feb 28, not March 3)
    if (endDate.getDate() !== startDate.getDate()) {
      endDate.setDate(0);
    }
  }

  return endDate.toISOString().split("T")[0];
};

  const updateLocalSession = () => {
    try {
      const sessionData = localStorage.getItem("user");
      if (!sessionData) return;
      const parsedUser = JSON.parse(sessionData);
      const updatedUser = {
        ...parsedUser,
        is_subscriber: 1,
        active_plan_id: plan_id,
        active_package_id: package_id,
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      window.dispatchEvent(new Event("storage"));
    } catch (e) {
      console.error(e);
    }
  };

  const handlePaymentComplete = async (paymentMethod, couponCode) => {
  setIsModalOpen(false);
  setLoading(true);
  
  const userId = getUserId();
  const today = new Date().toISOString().split("T")[0];
 const endDate = calculateEndDate(today, duration);

  try {
    // 1. Fetch User Profile for Gateway requirements (Just like Cart)
    const userDetailsRes = await fetch(SummaryApi.userDetailsById.url, {
      method: SummaryApi.userDetailsById.method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId }),
    });
    const userDetailsData = await userDetailsRes.json();
    
    if (!userDetailsData.status) {
      toast.error("Profile not found.");
      setLoading(false);
      return;
    }

    const profile = userDetailsData.data;

    // 2. Prepare Subscription Payload
    // This is the data OrderSuccess will send to SummaryApi.subscriptionCreate
    const subData = {
      user_id: userId,
      plan_id: plan_id,
      package_id: package_id,
      package_start_date: today,
      package_end_date: endDate,
      payment_mode: "Online",
      coupon_code: couponCode,
      amount: selling_price,
    };

    // 3. Request Payment Link from Gateway
    toast.loading("Preparing secure payment...");
    const paymentRes = await fetch(SummaryApi.createPaymentIntent.url, {
      method: SummaryApi.createPaymentIntent.method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...subData,
        amount: selling_price,
        email: profile.user_email || profile.email,
        phone: profile.user_mobile || profile.mobile_number || profile.mobile,
      }),
    });

    const paymentData = await paymentRes.json();
    toast.dismiss();

    if (paymentData.success && paymentData.checkout_url) {
      // 4. SAVE TO LOCAL STORAGE (OrderSuccess will look for this key)
      localStorage.setItem("pendingSubscription", JSON.stringify(subData));
      
      // 5. REDIRECT TO GATEWAY
      window.location.href = paymentData.checkout_url;
    } else {
      showCustomToast("error", "Error", paymentData.message || "Payment link error.");
    }
  } catch (error) {
    console.error("Subscription Payment Error:", error);
    showCustomToast("error", "Error", "Connection to payment gateway failed.");
  } finally {
    setLoading(false);
  }
};

  return (
    <>
      <div
        className={`relative bg-white rounded-3xl shadow-xl transition-all border-2 flex flex-col h-full ${isSubscribed ? "border-green-500 z-10" : isUsed ? "border-gray-100 opacity-80" : "border-transparent hover:border-pink-100"}`}
      >
        {isSubscribed && (
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-green-500 text-white text-[10px] font-black px-4 py-1.5 rounded-full z-20 uppercase flex items-center gap-1 shadow-lg">
            <CheckCircle2 size={12} /> My Current Plan
          </div>
        )}

        <div className="p-6 flex-1 flex flex-col">
          <div className="min-h-[90px] mb-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="text-2xl font-bold text-gray-800">{name}</h3>
                <span className="text-rose-500/90 mt-2 text-m font-medium">
                  {title}
                </span>
              </div>
              <ShieldCheck
                className={`w-6 h-6 ${isSubscribed ? "text-green-500" : "text-gray-300"}`}
              />
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">{subtitle}</p>
          </div>

          <div className="mb-6 h-16 flex flex-col justify-center">
            <div className="flex gap-3">
              <div className="text-3xl font-black text-pink-600">
                ₹{selling_price}
              </div>
              <p className="text-lg font-semibold text-rose-500/90 line-through">
                ₹{actual_price}
              </p>
            </div>
            <p className="text-[11px] font-bold mt-1 text-pink-400">
              Rate: {rate}
            </p>
          </div>

          <div
            className={`${isSubscribed ? "bg-green-50 text-green-800" : "bg-pink-50 text-pink-800"} rounded-xl p-3 mb-6 text-sm font-bold text-center`}
          >
            {padCount}
          </div>

          <div className="flex-1">
                  <ul className="space-y-3 mb-6">
              {displayedFeatures.length > 0 ? (
                displayedFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-[11px] text-gray-600 font-medium">
                    <CheckCircle2 size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span
                      className="leading-tight"
                      dangerouslySetInnerHTML={{ __html: f }} // RENDERS &#8377; CORRECTLY
                    />
                  </li>
                ))
              ) : (
                <li className="text-[11px] text-gray-400 italic">No specifications available</li>
              )}
              
              {!isPackagesRoute && processedFeatures.length > 4 && (
                <li className="ml-5 mt-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate("/packages"); }}
                    className="text-[11px] text-pink-600 font-bold hover:text-pink-700"
                  >
                    View all features <span>→</span>
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="p-6 pt-0 mt-auto">
          <button
            onClick={() => {
              if (isSubscribed) return;
              if (!canSubscribe) {
                showCustomToast(
                  "error",
                  "Subscription Limit",
                  "You already have an active plan.",
                );
                return;
              }
              if (getUserId()) {
                setIsModalOpen(true);
              } else {
                setIsAuthModalOpen(true); // Open Auth Modal instead of toast
              }
            }}
            disabled={loading || isSubscribed}
            className={`w-full py-4 rounded-xl font-bold transition-all text-xs uppercase tracking-widest flex justify-center items-center gap-2 ${isSubscribed ? "bg-green-500 text-white cursor-default" : isUsed ? "bg-pink-500 text-white hover:bg-pink-600 active:scale-95" : !canSubscribe ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-gray-900 text-white hover:bg-black active:scale-95"}`}
          >
            {loading ? (
              <Loader2 className="animate-spin w-4 h-4" />
            ) : isSubscribed ? (
              "Subscribed"
            ) : isUsed ? (
              <>
                <RefreshCw size={14} /> Renew Plan
              </>
            ) : (
              "Subscribe"
            )}
          </button>
        </div>
      </div>

      {/* --- AUTH MODAL --- */}
      <AnimatePresence>
        {isAuthModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl relative"
            >
              {/* Close Button */}
              <button
                onClick={() => setIsAuthModalOpen(false)}
                className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400"
              >
                <X size={20} />
              </button>

              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-pink-100 text-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Lock size={32} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Join Us
                </h3>
                <p className="text-gray-500 text-sm mb-8">
                  Please login or create an account to subscribe to the{" "}
                  <span className="font-bold text-gray-700">{name}</span>{" "}
                  package.
                </p>

                <div className="space-y-3">
                  <button
               onClick={() => handleAuthNavigation("/login")}
                    className="w-full py-4 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition-all"
                  >
                    Login to Account
                  </button>
                  <button
                     onClick={() => handleAuthNavigation("/signup")}
                    className="w-full py-4 bg-white text-pink-600 border-2 border-pink-100 rounded-xl font-bold hover:bg-pink-50 transition-all"
                  >
                    Create New Account
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <PaymentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        packageData={{
          name,
          actual_price,
          selling_price,
          padCount,
          endDate: calculateEndDate(
            new Date().toISOString().split("T")[0],
            duration,
          ),
        }}
        onPaymentSuccess={handlePaymentComplete}
      />
    </>
  );
};

export default PackageCard;
